import styled from "styled-components";
import { theme } from "../../theme/semanticTheme";

export const TabsWrap = styled.div`
  display: flex;
  flex-direction: ${(p) => (p.$orientation === "vertical" ? "row" : "column")};
  width: ${(p) => (p.$tabsOnly ? "auto" : "100%")};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.base};
`;

const SEGMENT_BAR_HEIGHT = 40;

export const TabListWrap = styled.div`
  display: flex;
  flex-direction: ${(p) => (p.$orientation === "vertical" ? "column" : "row")};
  gap: ${(p) => {
    if (p.$variant === "pills" || p.$variant === "segments") return theme.spacing.xs;
    if (p.$variant === "underline" && p.$orientation === "horizontal") return theme.spacing.lg;
    if (p.$variant === "underline" && p.$orientation === "vertical") return theme.spacing.md;
    return 0;
  }};
  border-bottom: ${(p) =>
    p.$variant === "underline" && p.$orientation === "horizontal" ? `2px solid ${theme.colors.border}` : "none"};
  border-right: ${(p) =>
    p.$variant === "underline" && p.$orientation === "vertical" ? `2px solid ${theme.colors.border}` : "none"};
  margin-bottom: ${(p) => (p.$orientation === "horizontal" ? theme.spacing.lg : 0)};
  margin-right: ${(p) => (p.$orientation === "vertical" ? theme.spacing.lg : 0)};
  flex-shrink: 0;

  /* default & underline: subtle purple tint to match app theme */
  ${(p) =>
    (p.$variant === "default" || p.$variant === "underline") &&
    `
    background: ${theme.sidebar.activeBg};
    padding: 0 ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.md};
    border: 1px solid ${theme.colors.border};
    box-shadow: 0 1px 2px rgba(57, 0, 149, 0.06);
  `}

  /* segments: pill group with background (Today/Week/Month style) */
  ${(p) =>
    p.$variant === "segments" &&
    `
    height: ${SEGMENT_BAR_HEIGHT}px;
    padding: 0 5px;
    background: ${theme.colors.backgroundAlt};
    border-radius: ${theme.borderRadius.md};
    border: 1px solid ${theme.colors.border};
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
    margin-bottom: 0;
    margin-right: 0;
    align-items: center;
    justify-content: center;
  `}
`;

export const TabButtonBase = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textMuted};
  background: none;
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  white-space: nowrap;
  flex: ${(p) => (p.$fullWidth ? "1 1 0" : "0 0 auto")};
  min-width: 0;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${theme.colors.purple};
  }

  &:hover:not(:disabled):not([aria-selected="true"]) {
    color: ${theme.colors.text};
    background: ${theme.colors.backgroundAlt};
  }
`;

export const TabButtonDefault = styled(TabButtonBase)`
  border-bottom: ${(p) => (p.$orientation === "horizontal" ? `2px solid transparent` : "none")};
  border-right: ${(p) => (p.$orientation === "vertical" ? `2px solid transparent` : "none")};
  margin-bottom: -2px;
  margin-right: ${(p) => (p.$orientation === "vertical" ? -2 : 0)}px;

  &[aria-selected="true"] {
    color: ${theme.colors.purple};
    border-color: ${theme.colors.purple};
  }
`;

export const TabButtonUnderline = styled(TabButtonBase)`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  border-bottom: ${(p) => (p.$orientation === "horizontal" ? `2px solid transparent` : "none")};
  border-right: ${(p) => (p.$orientation === "vertical" ? `2px solid transparent` : "none")};
  margin-bottom: -2px;
  margin-right: ${(p) => (p.$orientation === "vertical" ? -2 : 0)}px;

  &[aria-selected="true"] {
    color: ${theme.colors.purple};
    border-color: ${theme.colors.purple};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

export const TabButtonPills = styled(TabButtonBase)`
  &[aria-selected="true"] {
    color: #fff;
    background: ${theme.colors.purple};
  }
`;

export const TabButtonSegments = styled(TabButtonBase)`
  height: ${SEGMENT_BAR_HEIGHT - 8}px;
  font-size: ${theme.typography.fontSize.sm};
  padding: 0 ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  justify-content: center;
  align-items: center;
  line-height: 1;

  &[aria-selected="true"] {
    color: #fff;
    background: ${theme.colors.accent};
  }

  &:hover:not(:disabled):not([aria-selected="true"]) {
    color: ${theme.colors.primary};
  }
`;

export const TabButtonCard = styled(TabButtonBase)`
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.background};

  &[aria-selected="true"] {
    color: ${theme.colors.purple};
    border-color: ${theme.colors.purple};
    background: ${theme.colors.accentLight};
  }
`;

export const TabPanelWrap = styled.div`
  flex: 1;
  min-width: 0;
  outline: none;

  &:focus {
    outline: none;
  }
`;

export const CloseIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  font-size: 14px;
  line-height: 1;
  color: inherit;
  opacity: 0.7;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    opacity: 1;
  }
`;

export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.textMuted};
  background: none;
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  flex: ${(p) => (p.$fullWidth ? "1 1 0" : "0 0 auto")};
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: ${theme.colors.purple};
    border-color: ${theme.colors.purple};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${theme.colors.purple};
  }
`;
